import { ClearRounded, DeleteOutlineRounded, RefreshRounded, UploadRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import ExcelJS from "exceljs";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { useMemo, useState } from "react";
import { publishTableColumn } from "components/PimDashboard/data";

const normalizeHeader = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "_");

const parseCsvLine = (line) => {
	const values = [];
	let current = "";
	let inQuotes = false;

	for (let index = 0; index < line.length; index += 1) {
		const char = line[index];
		const nextChar = line[index + 1];

		if (char === '"') {
			if (inQuotes && nextChar === '"') {
				current += '"';
				index += 1;
			} else {
				inQuotes = !inQuotes;
			}
			continue;
		}

		if (char === "," && !inQuotes) {
			values.push(current.trim());
			current = "";
			continue;
		}

		current += char;
	}

	values.push(current.trim());
	return values;
};

const formatUploadValue = (value, column) => {
	if (value == null) return "";
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		const pad = (item) => String(item).padStart(2, "0");
		return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())} ${pad(value.getHours())}:${pad(
			value.getMinutes(),
		)}:${pad(value.getSeconds())}`;
	}

	const trimmedValue = typeof value === "string" ? value.trim() : value;

	if (column?.data_type === "DECIMAL" || column?.data_type === "NUMBER") {
		return trimmedValue === "" ? "" : Number(trimmedValue);
	}

	if (column?.data_type === "DATE" && typeof trimmedValue === "string") {
		if (trimmedValue === "") return "";
		if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
			return `${trimmedValue} 00:00:00`;
		}

		const parsedDate = new Date(trimmedValue);
		if (!Number.isNaN(parsedDate.getTime())) {
			const pad = (item) => String(item).padStart(2, "0");
			return `${parsedDate.getFullYear()}-${pad(parsedDate.getMonth() + 1)}-${pad(parsedDate.getDate())} ${pad(
				parsedDate.getHours(),
			)}:${pad(parsedDate.getMinutes())}:${pad(parsedDate.getSeconds())}`;
		}
	}

	return trimmedValue;
};

const UploadModal = ({ modal, setModal, columns = [], callbackFun, transformRows, transitionConfig = null, onRowsChange }) => {
	const [parsedRows, setParsedRows] = useState([]);
	const [fileName, setFileName] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const headerLookup = useMemo(() => {
		return columns.reduce((acc, column) => {
			acc[normalizeHeader(column.key)] = column;
			acc[normalizeHeader(column.name)] = column;
			return acc;
		}, {});
	}, [columns]);

	const updateRows = (updater) => {
		setParsedRows((prev) => {
			const nextRows = typeof updater === "function" ? updater(prev) : updater;
			if (typeof onRowsChange === "function") {
				onRowsChange(nextRows);
			}
			return nextRows;
		});
	};

	const resetState = () => {
		updateRows([]);
		setFileName("");
		setLoading(false);
		setError("");
	};

	const handleModalclose = () => {
		resetState();
		setModal(false);
	};

	const parseCsvFile = async (file) => {
		const text = await file.text();
		const lines = text.split(/\r?\n/).filter((line) => line.trim());
		if (!lines.length) return [];

		const rawHeaders = parseCsvLine(lines[0]);
		const mappedColumns = rawHeaders.map((header) => headerLookup[normalizeHeader(header)] || null);

		if (!mappedColumns.some((column) => column?.key === "i_code")) {
			throw new Error("Upload file must include the i_code column.");
		}

		return lines.slice(1).map((line) => {
			const values = parseCsvLine(line);
			return mappedColumns.reduce((acc, column, index) => {
				if (!column) return acc;
				acc[column.key] = formatUploadValue(values[index], column);
				return acc;
			}, {});
		});
	};

	const parseExcelFile = async (file) => {
		const workbook = new ExcelJS.Workbook();
		const buffer = await file.arrayBuffer();
		await workbook.xlsx.load(buffer);

		const worksheet = workbook.worksheets[0];
		if (!worksheet) return [];

		const headerRow = worksheet.getRow(1);
		const rawHeaders = headerRow.values.slice(1);
		const mappedColumns = rawHeaders.map((header) => headerLookup[normalizeHeader(header)] || null);

		if (!mappedColumns.some((column) => column?.key === "i_code")) {
			throw new Error("Upload file must include the i_code column.");
		}

		const rows = [];
		worksheet.eachRow((row, rowNumber) => {
			if (rowNumber === 1) return;

			const mappedRow = mappedColumns.reduce((acc, column, index) => {
				if (!column) return acc;
				acc[column.key] = formatUploadValue(row.getCell(index + 1).value, column);
				return acc;
			}, {});

			rows.push(mappedRow);
		});

		return rows;
	};

	const handleFileChange = async (event) => {
		const selectedFile = event.target.files?.[0];
		event.target.value = "";

		if (!selectedFile) return;

		setLoading(true);
		setError("");

		try {
			const isCsv = selectedFile.name.toLowerCase().endsWith(".csv");
			const rows = isCsv ? await parseCsvFile(selectedFile) : await parseExcelFile(selectedFile);
			const filteredRows = rows.filter((row) => Object.values(row).some((value) => value !== "" && value != null));
			const finalRows = typeof transformRows === "function" ? await transformRows(filteredRows) : filteredRows;

			updateRows(finalRows);
			setFileName(selectedFile.name);
		} catch (uploadError) {
			updateRows([]);
			setFileName("");
			setError(uploadError.message || "Unable to read the selected file.");
		} finally {
			setLoading(false);
		}
	};

	const handleFormSubmit = async (event) => {
		event.preventDefault();

		if (typeof callbackFun !== "function") return;

		setLoading(true);
		try {
			const shouldClose = await callbackFun(parsedRows, { transitionChecked: transitionConfig?.checked || false });
			if (shouldClose !== false) {
				handleModalclose();
			}
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteRow = (rowId) => {
		updateRows((prev) => prev.filter((row) => row.__rowId !== rowId));
	};

	const handleRefreshRows = async () => {
		if (typeof transformRows !== "function" || parsedRows.length === 0) return;

		setLoading(true);
		setError("");
		try {
			const refreshedRows = await transformRows(
				parsedRows.map(({ __isValid, __currentStatus, __note, __currentRow, ...row }) => row),
			);
			updateRows(refreshedRows);
		} catch (refreshError) {
			setError(refreshError.message || "Unable to refresh row statuses.");
		} finally {
			setLoading(false);
		}
	};

	const uploadPreviewColumns = useMemo(() => {
		const merchantStatusColumn = publishTableColumn(() => {}, true);

		return [
			{
				key: "__upload_status",
				name: "UPLOAD STATUS",
				headerAlign: "center",
				align: "center",
				minWidth: 120,
				maxWidth: 140,
				renderCell: ({ row }) => (
					<Box color={row.__isValid === false ? "error.main" : "success.main"} fontWeight={600}>
						{row.__isValid === false ? "Invalid" : "Valid"}
					</Box>
				),
			},
			{
				key: "__merchant_status",
				name: "MERCHANT STATUS",
				headerAlign: "center",
				align: "center",
				minWidth: 150,
				maxWidth: 180,
				renderCell: ({ row }) => merchantStatusColumn.renderCell({ row: { status: row.__currentStatus } }),
			},
			{
				key: "__note",
				name: "NOTE",
				headerAlign: "center",
				align: "center",
				minWidth: 180,
				maxWidth: 260,
				renderCell: ({ row }) => (
					<Typography
						variant="body2"
						color={row.__isValid === false ? "error.main" : "text.secondary"}
						fullWidth
						sx={{ width: "100%" }}
						textAlign="center">
						{row.__note || ""}
					</Typography>
				),
			},
			...columns.map((column) => ({
				...column,
				name: column.name,
				headerAlign: "center",
				align: "center",
				minWidth: 150,
				editable: true,
			})),
			{
				key: "__delete",
				name: "DELETE",
				headerAlign: "center",
				align: "center",
				minWidth: 90,
				maxWidth: 90,
				renderCell: ({ row }) => (
					<IconButton
						size="small"
						color="error"
						onClick={(event) => {
							event.stopPropagation();
							handleDeleteRow(row.__rowId);
						}}>
						<DeleteOutlineRounded fontSize="small" />
					</IconButton>
				),
			},
		];
	}, [columns]);

	return (
		<Dialog
			maxWidth="xl"
			fullWidth
			scroll="paper"
			open={modal}
			onClose={handleModalclose}
			component="form"
			onSubmit={handleFormSubmit}
			PaperProps={{ sx: { height: "85vh" } }}>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h6">Upload Merchant Data</Typography>
				<IconButton onClick={handleModalclose}>
					<ClearRounded />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
				<Stack spacing={2} sx={{ minHeight: 0, flex: 1 }}>
					<Alert severity="info">
						Use only the first sheet in Excel. Required column: <strong>i_code</strong>. Supported columns are the
						enterable merchant fields only.
					</Alert>

					<Box>
						<Stack direction="row" spacing={1} flexWrap="wrap">
							<Button
								component="label"
								variant="outlined"
								startIcon={loading ? <CircularProgress size={16} /> : <UploadRounded />}>
								Choose CSV / Excel
								<input hidden type="file" accept=".csv,.xlsx" onChange={handleFileChange} disabled={loading} />
							</Button>
							<Button
								variant="outlined"
								startIcon={loading ? <CircularProgress size={16} /> : <RefreshRounded />}
								onClick={handleRefreshRows}
								disabled={loading || parsedRows.length === 0}>
								Refresh Status
							</Button>
						</Stack>
						{fileName && (
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								{`${fileName} - ${parsedRows.length} rows read`}
							</Typography>
						)}
						{loading && (
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								Reading file and validating i_codes...
							</Typography>
						)}
					</Box>

					{error && <Alert severity="error">{error}</Alert>}

					{parsedRows.length > 0 && (
						<Box sx={{ flex: 1, minHeight: 260 }}>
							<TanstackDatagrid
								columns={uploadPreviewColumns}
								pageData={parsedRows}
								setPageData={updateRows}
								getRowId={(row) => row.__rowId}
								disablePagination={true}
								serverSidePagination={false}
								enableHorizontalScroll={true}
								headerHeight={40}
								rowHeight={42}
								processRowUpdate={(newRow) => newRow}
								isCellEditable={({ column }) =>
									!["__upload_status", "__merchant_status", "__note", "__delete"].includes(column.key)
								}
								children={
									transitionConfig ? (
										<Box display={"flex"} alignItems="center">
											<FormControlLabel
												control={
													<Checkbox
														checked={transitionConfig.checked}
														onChange={(event) => transitionConfig.onChange?.(event.target.checked)}
														disabled={loading || !transitionConfig.canEnable}
													/>
												}
												label={transitionConfig.label}
											/>
											{!transitionConfig.canEnable && transitionConfig.note && (
												<Typography variant="caption" color="error.main">
													(*{transitionConfig.note})
												</Typography>
											)}
										</Box>
									) : (
										<></>
									)
								}
							/>
						</Box>
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button variant="outlined" onClick={handleModalclose} disabled={loading}>
					Cancel
				</Button>
				<Button type="submit" variant="contained" disabled={loading || parsedRows.length === 0}>
					Save Upload
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UploadModal;
